import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { AdBanner } from '@/components/AdBanner';

const Terms = () => {
  return (
    <div className="min-h-screen gradient-surface gradient-mesh flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="glass-strong rounded-3xl p-6 lg:p-10 glow-border">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="w-12 h-12 rounded-2xl gradient-bg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold">Terms of Service</h1>
          </div>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6">
            <p className="text-muted-foreground">Last updated: December 30, 2024</p>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing and using TempVault, you accept and agree to be bound by the terms and provisions 
                of this agreement. If you do not agree to these terms, please do not use our service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                TempVault provides free temporary, disposable email addresses. These email addresses are 
                designed for short-term use and automatically expire after 24 hours. The service is provided 
                "as is" without any guarantees or warranties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Acceptable Use</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                You agree to use TempVault only for lawful purposes. You may NOT use our service to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Engage in any illegal activities</li>
                <li>Send spam or unsolicited messages</li>
                <li>Harass, abuse, or harm others</li>
                <li>Distribute malware or malicious content</li>
                <li>Attempt to gain unauthorized access to any systems</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Service Availability</h2>
              <p className="text-muted-foreground leading-relaxed">
                We strive to maintain high availability, but we do not guarantee uninterrupted service. 
                We may modify, suspend, or discontinue the service at any time without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                All emails and temporary inboxes are automatically deleted after 24 hours. We are not 
                responsible for any data loss. Do not use this service for important communications that 
                you need to retain.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                TempVault and its operators shall not be liable for any direct, indirect, incidental, 
                consequential, or exemplary damages arising from your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All content, trademarks, and intellectual property on TempVault are owned by us. 
                You may not copy, modify, or distribute any part of our service without permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these terms at any time. Continued use of the service 
                after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Advertising</h2>
              <p className="text-muted-foreground leading-relaxed">
                TempVault displays advertisements through Google AdSense to support our free service. 
                By using our service, you acknowledge that advertisements may be displayed and that 
                third-party advertising partners may use cookies to personalize ads based on your 
                browsing history.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms, please contact us through our{' '}
                <Link to="/contact" className="text-primary hover:underline">Contact page</Link>.
              </p>
            </section>

            {/* Ad Banner */}
            <div className="glass rounded-2xl p-4 mt-8">
              <AdBanner adFormat="horizontal" />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
